import React, {
  FunctionComponent,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
  useContext,
  useCallback,
} from "react";
import URI from "urijs";
import { Header } from "../../components/Header";
import classNames from "classnames";
import {
  useTranslate,
  sendTealiumEvent,
  makePlanUrlPartForApi,
  EnrollmentPages,
  getValidationObject,
  isOutsideOpenEnrollment,
  makePlanDisplayId,
  parseSearchParams,
  useQueryParams,
} from "../../helpers";
import CurrentFormPage from "./CurrentFormPage";
import { AppContext } from "../../app/store";
import { getPlan } from "../../api";
import {
  ActionType,
  Plan,
  Title,
  SearchResultPlan,
  Sex,
  EnrollData,
  Gender,
  GetBenefitsFrom,
  EnrollData2021,
  EnrollData2022,
} from "../../@types";
import { MCTDateFromString } from "../../helpers/dateHelpers";
import { RouteComponentProps } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import states from "./states";
import { plan } from "../../__mocks__";
import { isCostPlanWithoutDrugs } from "../../helpers/enrollmentFormHelpers";

export interface EnrollmentFormProps {
  enrollData: EnrollData;
  setEnrollData: Dispatch<SetStateAction<EnrollData>>;
  nextPage: () => void;
  previousPage: () => void;
  plan: Plan;
  goToPage: (page: EnrollmentPages) => void;
  inReview: boolean;
}

export interface EnrollmentFormProps2021 {
  enrollData: EnrollData2021;
  setEnrollData: Dispatch<SetStateAction<EnrollData2021>>;
  nextPage: () => void;
  previousPage: () => void;
  plan: Plan;
  goToPage: (page: EnrollmentPages) => void;
  inReview: boolean;
}

export interface EnrollmentFormProps2022 {
  enrollData: EnrollData2022;
  setEnrollData: Dispatch<SetStateAction<EnrollData2022>>;
  nextPage: () => void;
  previousPage: () => void;
  plan: Plan;
  goToPage: (page: EnrollmentPages) => void;
  inReview: boolean;
}

export const emptyEnrollData: EnrollData = {
  sepOtherReason: "",
  hasSepOtherReason: false,
  sepReasons: [],
  medicareNumber: "",
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  partAStart: { day: "", month: "", year: "" },
  partBStart: { day: "", month: "", year: "" },
  title: Title.NONE,
  firstName: "",
  middleInitial: "",
  lastName: "",
  dob: { day: "", month: "", year: "" },
  sex: Sex.NONE,
  email: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  emergencyContactName: "",
  emergencyContactPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  emergencyContactRelationship: "",
  address1: "",
  address2: "",
  zip: "",
  state: "",
  city: "",
  county: undefined,
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  longTermCareFacility: {
    name: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  primaryCareName: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
};

export const emptyEnrollData2021: EnrollData2021 = {
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  address1: "",
  address2: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  city: "",
  county: undefined,
  dob: { day: "", month: "", year: "" },
  email: "",
  firstName: "",
  hasSepOtherReason: false,
  lastName: "",
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  medicareNumber: "",
  middleInitial: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  primaryCareName: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  sepOtherReason: "",
  sepReasons: [],
  sex: Sex.NONE,
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  state: "",
  zip: "",
};

export const emptyEnrollData2022: EnrollData2022 = {
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  address1: "",
  address2: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  city: "",
  county: undefined,
  dob: { day: "", month: "", year: "" },
  email: "",
  firstName: "",
  hasSepOtherReason: false,
  lastName: "",
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  medicareNumber: "",
  middleInitial: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  primaryCareName: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  sepOtherReason: "",
  sepReasons: [],
  sex: Sex.NONE,
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  state: "",
  zip: "",
};

export const EnrollOptionalLabel: FunctionComponent<{ label: string }> = ({
  label,
}) => {
  const t = useTranslate();

  return (
    <>
      <span>{label}</span>{" "}
      <span className="ds-u-text-transform--lowercase">
        {t("enroll_form.optional")}
      </span>
    </>
  );
};

const EnrollmentFormPage: FunctionComponent<RouteComponentProps> = ({
  history,
  location,
  match,
}) => {
  const t = useTranslate();
  const {
    state: { selectedPlan, beneficiary, zipcode, county, language },
    dispatch,
  } = useContext(AppContext);
  const [orderedPages, setOrderedPages] = useState<EnrollmentPages[]>([]);
  const [stepPages, setStepPages] = useState<EnrollmentPages[]>([]);
  const [includeSep, setIncludeSep] = useState(false);
  useQueryParams({ history, location, match });

  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
    mctCurrentPlanYear,
  } = useFlags();

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  let defaultEnrollData: EnrollData | EnrollData2021 | EnrollData2022;

  if (selectedPlan?.contract_year === "2021") {
    defaultEnrollData = emptyEnrollData2021;
  } else if (selectedPlan?.contract_year === "2022") {
    defaultEnrollData = emptyEnrollData2022;
  } else {
    defaultEnrollData = emptyEnrollData;
  }

  if (beneficiary) {
    let sex = Sex.NONE;

    if (beneficiary.gender === Gender.FEMALE) {
      sex = Sex.FEMALE;
    } else if (beneficiary.gender === Gender.MALE) {
      sex = Sex.MALE;
    }

    let address1 = "";
    let address2 = "";
    let city = "";
    let state = "";

    if (beneficiary.mailing_address) {
      const {
        address_1,
        address_2,
        city: beneCity,
        state: beneState,
      } = beneficiary.mailing_address;
      address1 = address_1;
      address2 = address_2;
      city = beneCity;

      const stateObj = states.find(state => state.abbreviation == beneState);

      if (stateObj) {
        state = stateObj.fipsCode;
      }
    }

    defaultEnrollData = {
      ...defaultEnrollData,
      firstName:
        beneficiary.firstname && beneficiary.firstname.length
          ? beneficiary.firstname[0]
          : "",
      middleInitial:
        beneficiary.firstname.length > 1 && beneficiary.firstname[1].length
          ? beneficiary.firstname[1][0].trim()
          : "",
      lastName: beneficiary.lastname,
      dob: MCTDateFromString(beneficiary.birthdate),
      medicareNumber: beneficiary.mbi ? beneficiary.mbi : "",
      ssn: {
        areaNumber: beneficiary.ssn?.substring(0, 3) || "",
        groupNumber: beneficiary.ssn?.substring(3, 5) || "",
        serialNumber: beneficiary.ssn?.substring(5, 9) || "",
      },
      sex,
      address1,
      address2,
      city,
      state,
      email: beneficiary.email || "",
    };

    if (plan.contract_year === "2020") {
      defaultEnrollData = {
        ...defaultEnrollData,
        partAStart: MCTDateFromString(beneficiary.part_a_start),
        partBStart: MCTDateFromString(beneficiary.part_b_start),
      };
    }
  }

  if (zipcode) {
    defaultEnrollData = {
      ...defaultEnrollData,
      zip: zipcode,
      county,
    };
  }

  const [enrollData, setEnrollData] = useState<
    EnrollData | EnrollData2021 | EnrollData2022
  >(defaultEnrollData);
  const [currentPage, setCurrentPage] = useState(EnrollmentPages.MedicareInfo);
  const [inReview, setInReview] = useState(false);
  const [needPlanCard, setNeedPlanCard] = useState(false);
  const [stepsTaken, setStepsTaken] = useState(1);

  const goToPage = useCallback(
    (page: EnrollmentPages, replace?: boolean): void => {
      if (page === EnrollmentPages.Review) {
        setInReview(true);
      }

      const uri = new URI(location.search);
      uri.setQuery("page", page || currentPage);
      location.search = uri.search();
      if (replace) {
        history.replace(location);
      } else {
        setStepsTaken(stepsTaken + 1);
        history.push(location);
      }
    },
    [location, language]
  );

  useEffect(() => {
    if (selectedPlan) {
      const stepPagesByYear = {
        "2020": [
          EnrollmentPages.MedicareInfo,
          EnrollmentPages.PersonalInfo,
          EnrollmentPages.Address,
          EnrollmentPages.GeneralQuestions,
          EnrollmentPages.Paying,
          EnrollmentPages.Agreement,
        ],
        "2021": [
          EnrollmentPages.MedicareInfo,
          EnrollmentPages.PersonalInfo,
          EnrollmentPages.Address,
          EnrollmentPages.GeneralQuestions,
          EnrollmentPages.Paying,
          EnrollmentPages.Agreement,
          EnrollmentPages.Review,
        ],
        "2022": [
          EnrollmentPages.MedicareInfo,
          EnrollmentPages.PersonalInfo,
          EnrollmentPages.Address,
          EnrollmentPages.GeneralQuestions,
          EnrollmentPages.Paying,
          EnrollmentPages.Agreement,
          EnrollmentPages.Review,
        ],
      };

      const orderedPagesByYear = {
        "2020": [
          ...stepPagesByYear["2020"],
          EnrollmentPages.Review,
          EnrollmentPages.Enrolled,
        ],
        "2021": [...stepPagesByYear["2021"], EnrollmentPages.Enrolled],
        "2022": [...stepPagesByYear["2022"], EnrollmentPages.Enrolled],
      };

      let ordered = orderedPagesByYear[selectedPlan.contract_year];

      const selectedPlanIsCurrentPlanYear =
        mctCurrentPlanYear.toString() === selectedPlan.contract_year;

      const selectedPlanIs2021CostPlanWithoutDrugs =
        (selectedPlan.contract_year === "2021" ||
          selectedPlan.contract_year === "2022") &&
        isCostPlanWithoutDrugs(selectedPlan as Plan);

      const includeSepPage =
        (outsideOpenEnrollment || selectedPlanIsCurrentPlanYear) &&
        !selectedPlanIs2021CostPlanWithoutDrugs;

      setIncludeSep(includeSepPage);

      if (includeSepPage) {
        ordered = [EnrollmentPages.Sep, ...ordered];
      }

      setOrderedPages(ordered);
      setStepPages(stepPagesByYear[selectedPlan.contract_year]);
      setCurrentPage(ordered[0]);
    } else {
      history.push("/");
    }

    // eslint-disable-next-line no-prototype-builtins
    if (selectedPlan && !selectedPlan.hasOwnProperty("address_1")) {
      setNeedPlanCard(true);
      const apiUrl = makePlanUrlPartForApi(selectedPlan as SearchResultPlan);

      getPlan(apiUrl).then((plan: Plan | undefined) => {
        dispatch({
          type: ActionType.UPDATE_SELECTED_PLAN,
          payload: plan,
        });
        setNeedPlanCard(false);
      });
    }
  }, []);

  useEffect(() => {
    const uri = new URI(location.search);
    const { page: pageInUrl } = parseSearchParams(uri.search(true));
    let page = orderedPages[0];

    if (orderedPages.includes(pageInUrl as EnrollmentPages)) {
      const pageIndex = orderedPages.indexOf(pageInUrl as EnrollmentPages);
      if (pageIndex > 0 && selectedPlan) {
        const validityOfPreviousPage = getValidationObject(
          enrollData,
          selectedPlan,
          orderedPages[pageIndex - 1]
        );
        if (validityOfPreviousPage.isValid) {
          page = orderedPages[pageIndex];
        } else {
          goToPage(page, true);
        }
      }
    } else {
      goToPage(page, true);
    }
    setCurrentPage(page);
  }, [location.search]);

  const includeSteps = stepPages.includes(currentPage);
  const currentPageIndex = orderedPages.indexOf(currentPage);

  const nextPage = useCallback((): void => {
    const currentPageIndex = orderedPages.indexOf(currentPage);
    if (selectedPlan) {
      sendTealiumEvent(
        `Step ${currentPageIndex + 1} Enrollment - ${currentPage}`,
        `Step ${currentPageIndex + 1} - ${
          currentPage === EnrollmentPages.Review ? "Submit" : "Next"
        } - ${selectedPlan.name} - ${selectedPlan.plan_type}`,
        {
          plan_name: selectedPlan.name,
          plan_id: makePlanDisplayId(selectedPlan),
          plan_type: selectedPlan.plan_type,
        }
      );
    }

    if (currentPageIndex !== -1) {
      const nextPageIndex = currentPageIndex + 1;
      const nextPage = orderedPages[nextPageIndex];

      if (nextPage) {
        if (nextPage === EnrollmentPages.Review) {
          setInReview(true);
        }

        if (inReview && currentPage !== EnrollmentPages.Review) {
          goToPage(EnrollmentPages.Review);
        } else {
          goToPage(nextPage);
        }
      }
    }
  }, [orderedPages, selectedPlan, currentPage, inReview, language]);

  const previousPage = useCallback((): void => {
    const currentPageIndex = orderedPages.indexOf(currentPage);
    if (selectedPlan) {
      sendTealiumEvent(
        `Step ${currentPageIndex + 1} Enrollment - ${currentPage}`,
        `Step ${currentPageIndex + 1} - Back - ${selectedPlan.name} - ${
          selectedPlan.plan_type
        }`,
        {
          plan_name: selectedPlan.name,
          plan_id: makePlanDisplayId(selectedPlan),
          plan_type: selectedPlan.plan_type,
        }
      );
    }

    if (currentPageIndex === 0) {
      history.go(stepsTaken * -1);
    } else if (currentPageIndex !== -1) {
      const previousPageIndex = currentPageIndex - 1;
      const previousPage = orderedPages[previousPageIndex];

      if (previousPage) {
        if (previousPage === EnrollmentPages.Review) {
          setInReview(true);
        }

        goToPage(previousPage);
      }
    }
  }, [orderedPages, selectedPlan, currentPage, language]);

  const stepIndex = includeSep ? currentPageIndex : currentPageIndex + 1;

  return (
    <div className="EnrollmentFormPage">
      <Header />
      <div className="EnrollmentFormPage__content">
        {includeSteps && (
          <div>
            <ul className="EnrollmentFormPage__steps">
              {stepPages.map((_page, pageIndex) => {
                return (
                  <li
                    key={pageIndex}
                    className={classNames("EnrollmentFormPage__step", {
                      "EnrollmentFormPage__step--active": pageIndex < stepIndex,
                    })}
                  ></li>
                );
              })}
            </ul>
            <p className="EnrollmentFormPage__stepsText">
              {t("enrollment_form.step")} {stepIndex} {t("of")}{" "}
              {stepPages.length}
            </p>
          </div>
        )}

        {selectedPlan && !needPlanCard && (
          <CurrentFormPage
            enrollData={enrollData}
            setEnrollData={setEnrollData}
            nextPage={nextPage}
            previousPage={previousPage}
            currentPage={currentPage}
            plan={selectedPlan as Plan}
            goToPage={goToPage}
            inReview={inReview}
          />
        )}
      </div>
    </div>
  );
};

export default EnrollmentFormPage;
